import { useState, useEffect, useRef } from "react";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  X,
  Trash2,
  ExternalLink,
  Loader,
  Edit,
} from "lucide-react";
import { Link } from "react-router-dom";
import { apiDelete, apiGet, apiPost, apiPut } from "../../services/api";
import { useNotification } from "../../contexts/NotificationContext";

export default function MenuEditor() {
  const [menuData, setMenuData] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null,
    parentId: null,
    sectionId: null,
    pageId: null,
  });
  const [formData, setFormData] = useState({
    titles: {
      en: "",
      ru: "",
      kz: "",
    },
    route: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [loadingPageData, setLoadingPageData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supportedLanguages, setSupportedLanguages] = useState({
    en: "English",
    ru: "Русский",
    kz: "Қазақша",
  });
  const { showNotification } = useNotification();

  const submitTimeoutRef = useRef(null);
  const requestIdRef = useRef(0); // Добавляем счетчик запросов

  useEffect(() => {
    fetchMenuData();
    fetchSupportedLanguages();

    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  const fetchMenuData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet(
        `${process.env.REACT_APP_API_URL}/api/menu/structure`
      );

      if (response.data && response.data.menuStructure) {
        const formattedData = transformApiDataToLocalFormat(
          response.data.menuStructure
        );
        setMenuData(formattedData);
      }
    } catch (err) {
      console.error("Error fetching menu data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSupportedLanguages = async () => {
    try {
      const response = await apiGet(
        `${process.env.REACT_APP_API_URL}/api/languages`
      );
      if (response.data && response.data.languages) {
        setSupportedLanguages(response.data.languages);
      }
    } catch (err) {
      console.error("Error fetching languages:", err);
    }
  };

  const transformApiDataToLocalFormat = (apiData) => {
    return apiData.map((parent) => {
      const parentItem = {
        id: parent.id,
        type: "parent",
        title: parent.titles
          ? parent.titles.ru ||
          parent.titles.en ||
          parent.titles.kz ||
          "Без названия"
          : "Без названия",
        titles: parent.titles || { en: "", ru: "", kz: "" },
        items: [],
      };

      if (parent.items && parent.items.length > 0) {
        parentItem.items = parent.items.map((item) => {
          if (item.type === "section") {
            const sectionItem = {
              id: item.id,
              type: "section",
              title: item.titles
                ? item.titles.ru ||
                item.titles.en ||
                item.titles.kz ||
                "Без названия"
                : "Без названия",
              titles: item.titles || { en: "", ru: "", kz: "" },
              items: [],
            };

            if (item.sectionItems && item.sectionItems.length > 0) {
              sectionItem.items = item.sectionItems.map((subItem) => ({
                id: subItem.id,
                type: "item",
                title: subItem.titles
                  ? subItem.titles.ru ||
                  subItem.titles.en ||
                  subItem.titles.kz ||
                  "Без названия"
                  : "Без названия",
                titles: subItem.titles || { en: "", ru: "", kz: "" },
                route: subItem.route,
                pageId: subItem.pageId,
              }));
            }

            return sectionItem;
          } else {
            return {
              id: item.id,
              type: "item",
              title: item.titles
                ? item.titles.ru ||
                item.titles.en ||
                item.titles.kz ||
                "Без названия"
                : "Без названия",
              titles: item.titles || { en: "", ru: "", kz: "" },
              route: item.route,
              pageId: item.pageId,
            };
          }
        });
      }

      return parentItem;
    });
  };

  const toggleExpand = (type, id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [`${type}-${id}`]: !prev[`${type}-${id}`],
    }));
  };

  const openModal = async (
    type,
    parentId = null,
    sectionId = null,
    pageId = null
  ) => {
    console.log("🔧 Открываем модальное окно:", {
      type,
      parentId,
      sectionId,
      pageId,
    });

    setModalState({
      isOpen: true,
      type,
      parentId,
      sectionId,
      pageId,
    });

    if (type === "editPage" && pageId) {
      console.log("📝 Режим редактирования страницы, pageId:", pageId);
      setLoadingPageData(true);

      setFormData({
        titles: { en: "", ru: "", kz: "" },
        route: "",
      });

      await loadPageData(pageId);
    } else if (type === "editPage" && !pageId) {
      console.log("⚠️ Попытка редактирования страницы без pageId");
      alert(
        "Невозможно редактировать: у этого пункта меню нет связанной страницы"
      );
      return;
    } else {
      console.log("🆕 Режим создания нового элемента");
      setFormData({
        titles: { en: "", ru: "", kz: "" },
        route: "",
      });
    }
  };

  const loadPageData = async (pageId) => {
    try {
      console.log("🔍 Загружаем данные страницы для ID:", pageId);

      if (!pageId) {
        console.error("❌ ID страницы отсутствует");
        alert("ID страницы не найден");
        setLoadingPageData(false);
        return;
      }

      const response = await apiGet(
        `${process.env.REACT_APP_API_URL}/api/pages/${pageId}`
      );
      console.log("📡 Ответ от API:", response.data);

      const pageData = response.data;

      let titles = { en: "", ru: "", kz: "" };

      if (pageData.title && pageData.title.texts) {
        console.log("📝 Обрабатываем заголовки:", pageData.title.texts);
        titles = { ...titles, ...pageData.title.texts };
      }

      let route = "";
      if (pageData.slug) {
        route = pageData.slug.startsWith("/")
          ? pageData.slug
          : `/${pageData.slug}`;
        console.log("🛣️ Маршрут из slug:", route);
      }

      const newFormData = {
        titles: titles,
        route: route,
      };

      console.log("🎯 Устанавливаем данные в форму:", newFormData);
      setFormData(newFormData);
      console.log("✅ Данные страницы успешно загружены и установлены в форму");
    } catch (err) {
      console.error("❌ Ошибка при загрузке данных страницы:", err);
      console.error("📄 Детали ошибки:", err.response?.data);
      alert(
        `Ошибка при загрузке данных страницы: ${err.response?.data?.message || err.message
        }`
      );
    } finally {
      setLoadingPageData(false);
    }
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      type: null,
      parentId: null,
      sectionId: null,
      pageId: null,
    });
    setLoadingPageData(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTitleChange = (languageCode, value) => {
    setFormData((prev) => ({
      ...prev,
      titles: {
        ...prev.titles,
        [languageCode]: value,
      },
    }));
  };

  const createPageWithMultipleTitles = async (pageSlug, titles) => {
    try {
      const titlesObject = {};
      Object.entries(titles).forEach(([lang, text]) => {
        if (text && text.trim()) {
          titlesObject[lang] = text.trim();
        }
      });

      console.log("📝 Создаем страницу с данными:", { pageSlug, titlesObject });

      const pageResponse = await apiPost(
        `${process.env.REACT_APP_API_URL}/api/pages`,
        {
          slug: pageSlug,
          titles: titlesObject,
        }
      );

      if (!pageResponse.data || !pageResponse.data.id) {
        throw new Error("Не удалось создать страницу");
      }

      console.log("✅ Страница создана:", pageResponse.data);

      return {
        pageId: pageResponse.data.id,
        slug: pageSlug,
        titles: titlesObject,
      };
    } catch (error) {
      console.error("❌ Ошибка при создании страницы:", error);
      throw error;
    }
  };

  const saveMenuItem = async () => {
    // СУПЕР ВАЖНАЯ ЗАЩИТА - увеличиваем счетчик запросов
    const currentRequestId = ++requestIdRef.current;
    console.log(`🚀 Начинаем запрос #${currentRequestId}`);

    // Защита от двойных вызовов
    if (isSubmitting) {
      console.log(`⚠️ Запрос #${currentRequestId} отменен - уже выполняется сохранение`);
      return;
    }

    setIsSubmitting(true);

    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
    }

    const { type, parentId, sectionId, pageId } = modalState;
    setSaveStatus("saving");

    console.log(`📋 Запрос #${currentRequestId} - данные для сохранения:`, {
      type,
      parentId,
      sectionId,
      pageId,
      formData
    });

    try {
      const hasTitle = Object.values(formData.titles).some(
        (title) => title && title.trim()
      );
      if (!hasTitle) {
        console.log(`❌ Запрос #${currentRequestId} - нет заголовков`);
        alert("Заполните хотя бы один заголовок");
        setSaveStatus(null);
        return;
      }

      if (type === "parent") {
        console.log(`🔵 Запрос #${currentRequestId} - создаем родительский элемент`);

        const response = await apiPost(
          `${process.env.REACT_APP_API_URL}/api/menu`,
          {
            type: "parent",
            titles: formData.titles,
          }
        );

        console.log(`✅ Запрос #${currentRequestId} - родительский элемент создан:`, response.data);

        if (response.data && response.data.menuItem) {
          const newParent = {
            id: response.data.menuItem.id,
            type: "parent",
            title: response.data.menuItem.titles
              ? response.data.menuItem.titles.ru ||
              response.data.menuItem.titles.en ||
              response.data.menuItem.titles.kz ||
              "Без названия"
              : "Без названия",
            titles: response.data.menuItem.titles || formData.titles,
            items: [],
          };

          setMenuData((prev) => [...prev, newParent]);
        }
      } else if (type === "item") {
        console.log(`🔵 Запрос #${currentRequestId} - создаем элемент меню`);

        if (!formData.route || !formData.route.trim()) {
          console.log(`❌ Запрос #${currentRequestId} - нет маршрута`);
          alert("Путь (route) обязателен");
          setSaveStatus(null);
          return;
        }

        const routePath = formData.route.startsWith("/")
          ? formData.route
          : `/${formData.route}`;

        try {
          const slug = routePath.substring(1);
          let pageData = null;

          console.log(`🔍 Запрос #${currentRequestId} - проверяем страницу с slug:`, slug);

          // Backend сам проверит дубликаты, не нужно проверять во фронтенде
          console.log(`🔍 Запрос #${currentRequestId} - backend проверит дубликаты автоматически`);

          try {
            const existingPageResponse = await apiGet(
              `${process.env.REACT_APP_API_URL}/api/pages/by-slug/${slug}`
            );

            if (existingPageResponse.data && existingPageResponse.data.id) {
              console.log(`📄 Запрос #${currentRequestId} - найдена существующая страница:`, existingPageResponse.data.id);
              pageData = {
                pageId: existingPageResponse.data.id,
                slug: slug,
                titles: formData.titles,
              };

              console.log(`ℹ️ Запрос #${currentRequestId} - используем существующую страницу без изменений`);
            }
          } catch (notFoundError) {
            if (notFoundError.response?.status === 404) {
              console.log(`📝 Запрос #${currentRequestId} - страница не найдена, создаем новую`);
              pageData = await createPageWithMultipleTitles(slug, formData.titles);
              console.log(`✅ Запрос #${currentRequestId} - новая страница создана:`, pageData.pageId);
            } else {
              throw notFoundError;
            }
          }

          const menuItemData = {
            type: "item",
            titles: formData.titles,
            route: routePath,
            parentId: parentId,
            pageId: pageData.pageId,
          };

          if (sectionId) {
            menuItemData.sectionId = sectionId;
          }

          console.log(`📝 Запрос #${currentRequestId} - отправляем данные для создания элемента меню:`, menuItemData);

          const response = await apiPost(
            `${process.env.REACT_APP_API_URL}/api/menu`,
            menuItemData
          );

          console.log(`✅ Запрос #${currentRequestId} - элемент меню создан:`, response.data);

          if (response.data && response.data.menuItem) {
            // Обновляем локальное состояние
            setMenuData((prev) => {
              return prev.map((parent) => {
                if (parent.id === parentId) {
                  if (sectionId) {
                    const updatedItems = parent.items.map((item) => {
                      if (item.id === sectionId && item.type === "section") {
                        return {
                          ...item,
                          items: [
                            ...item.items,
                            {
                              id: response.data.menuItem.id,
                              type: "item",
                              title: response.data.menuItem.titles
                                ? response.data.menuItem.titles.ru ||
                                response.data.menuItem.titles.en ||
                                response.data.menuItem.titles.kz ||
                                "Без названия"
                                : "Без названия",
                              titles:
                                response.data.menuItem.titles ||
                                formData.titles,
                              route: routePath,
                              pageId: pageData.pageId,
                            },
                          ],
                        };
                      }
                      return item;
                    });
                    return { ...parent, items: updatedItems };
                  } else {
                    return {
                      ...parent,
                      items: [
                        ...parent.items,
                        {
                          id: response.data.menuItem.id,
                          type: "item",
                          title: response.data.menuItem.titles
                            ? response.data.menuItem.titles.ru ||
                            response.data.menuItem.titles.en ||
                            response.data.menuItem.titles.kz ||
                            "Без названия"
                            : "Без названия",
                          titles:
                            response.data.menuItem.titles || formData.titles,
                          route: routePath,
                          pageId: pageData.pageId,
                        },
                      ],
                    };
                  }
                }
                return parent;
              });
            });
          }
        } catch (err) {
          console.error(`❌ Запрос #${currentRequestId} - ошибка при создании:`, err);

          // Проверяем, является ли это ошибкой дублирования
          if (err.response?.status === 409) {
            alert("Элемент меню с таким маршрутом уже существует в данном разделе!");
          } else {
            alert(err.response?.data?.message || "Произошла ошибка при создании");
          }

          setSaveStatus("error");
          return;
        }
      } else if (type === "section" && parentId) {
        console.log(`🔵 Запрос #${currentRequestId} - создаем секцию`);

        const response = await apiPost(
          `${process.env.REACT_APP_API_URL}/api/menu`,
          {
            type: "section",
            titles: formData.titles,
            parentId: parentId,
          }
        );

        console.log(`✅ Запрос #${currentRequestId} - секция создана:`, response.data);

        if (response.data && response.data.menuItem) {
          setMenuData((prev) => {
            return prev.map((parent) => {
              if (parent.id === parentId) {
                return {
                  ...parent,
                  items: [
                    ...parent.items,
                    {
                      id: response.data.menuItem.id,
                      type: "section",
                      title: response.data.menuItem.titles
                        ? response.data.menuItem.titles.ru ||
                        response.data.menuItem.titles.en ||
                        response.data.menuItem.titles.kz ||
                        "Без названия"
                        : "Без названия",
                      titles: response.data.menuItem.titles || formData.titles,
                      items: [],
                    },
                  ],
                };
              }
              return parent;
            });
          });
        }
      } else if (type === "editPage") {
        console.log(`🔵 Запрос #${currentRequestId} - редактируем страницу`);

        try {
          const slugToUpdate = formData.route.startsWith("/")
            ? formData.route.substring(1)
            : formData.route;

          await apiPut(
            `${process.env.REACT_APP_API_URL}/api/pages/${pageId}`,
            {
              slug: slugToUpdate,
            }
          );

          const titlesObject = {};
          Object.entries(formData.titles).forEach(([lang, text]) => {
            if (text && text.trim()) {
              titlesObject[lang] = text.trim();
            }
          });

          await apiPut(
            `${process.env.REACT_APP_API_URL}/api/pages/${pageId}/titles`,
            {
              titles: titlesObject,
            }
          );

          console.log(`✅ Запрос #${currentRequestId} - страница обновлена`);
          await fetchMenuData();
        } catch (err) {
          console.error(`❌ Запрос #${currentRequestId} - ошибка при обновлении:`, err);
          alert(err.response?.data?.message || "Произошла ошибка при обновлении");
          setSaveStatus("error");
          return;
        }
      }

      console.log(`🎉 Запрос #${currentRequestId} - успешно завершен`);
      setSaveStatus("success");
      closeModal();
      showNotification("Пункт меню успешно сохранен", "success");
    } catch (err) {
      console.error(`❌ Запрос #${currentRequestId} - общая ошибка:`, err);
      setSaveStatus("error");
      if (err.response?.data?.message) {
        alert(`Ошибка: ${err.response.data.message}`);
      }
    } finally {
      console.log(`🏁 Запрос #${currentRequestId} - завершение, сброс флага через 3 секунды`);
      submitTimeoutRef.current = setTimeout(() => {
        console.log(`🔓 Запрос #${currentRequestId} - флаг isSubmitting сброшен`);
        setIsSubmitting(false);
      }, 3000); // Увеличиваем время до 3 секунд
    }
  };

  const deleteMenuItem = async (
    type,
    id,
    parentId = null,
    sectionId = null
  ) => {
    try {
      await apiDelete(`${process.env.REACT_APP_API_URL}/api/menu/${id}`);

      if (type === "parent") {
        setMenuData((prev) => prev.filter((parent) => parent.id !== id));
      } else if (type === "section" || type === "item") {
        if (parentId && !sectionId) {
          setMenuData((prev) => {
            return prev.map((parent) => {
              if (parent.id === parentId) {
                return {
                  ...parent,
                  items: parent.items.filter((item) => item.id !== id),
                };
              }
              return parent;
            });
          });
        } else if (parentId && sectionId) {
          setMenuData((prev) => {
            return prev.map((parent) => {
              if (parent.id === parentId) {
                const updatedItems = parent.items.map((item) => {
                  if (item.id === sectionId && item.type === "section") {
                    return {
                      ...item,
                      items: item.items.filter((subItem) => subItem.id !== id),
                    };
                  }
                  return item;
                });

                return { ...parent, items: updatedItems };
              }
              return parent;
            });
          });
        }
      }
      showNotification("Пункт меню успешно удален", "success");
    } catch (err) {
      console.error("Error deleting menu item:", err);
      setError("Ошибка при удалении пункта меню");
      showNotification("Ошибка при удалении пункта меню", "error");
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-2 font-sans">
        <div className="flex flex-col items-center justify-center h-48">
          <Loader
            size={24}
            className="animate-spin"
            style={{ color: "var(--primary-color)" }}
          />
          <p className="mt-3 text-gray-500 text-xs">
            Загрузка структуры меню...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-2 font-sans">
      <h1
        className="text-lg font-semibold text-gray-800 text-center mb-4 pb-2 border-b-2"
        style={{ borderColor: "var(--primary-color)" }}
      >
        Управление структурой меню
      </h1>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded mb-3 text-xs">
          <strong>Ошибка!</strong> {error}
        </div>
      )}

      {saveStatus === "success" && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded mb-3 text-xs">
          Изменения сохранены успешно!
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border p-3 mb-4">
        {menuData.map((parent) => (
          <div key={parent.id}>
            <div className="flex justify-between items-center p-2 bg-white rounded border border-gray-200 mb-2 hover:bg-gray-50">
              <div
                className="flex items-center flex-grow cursor-pointer"
                onClick={() => toggleExpand("parent", parent.id)}
              >
                {expandedItems[`parent-${parent.id}`] ? (
                  <ChevronDown
                    size={12}
                    className="mr-2"
                    style={{ color: "var(--primary-color)" }}
                  />
                ) : (
                  <ChevronRight
                    size={12}
                    className="mr-2"
                    style={{ color: "var(--primary-color)" }}
                  />
                )}
                <span className="font-medium text-gray-800 text-xs">
                  {parent.title}
                </span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteMenuItem("parent", parent.id);
                }}
                className="text-gray-400 hover:text-red-500 hover:bg-gray-50 p-1 rounded"
              >
                <Trash2 size={12} />
              </button>
            </div>

            {expandedItems[`parent-${parent.id}`] && (
              <div className="ml-5 mt-1">
                {parent.items.map((item) => (
                  <div key={item.id}>
                    {item.type === "item" ? (
                      <div className="flex justify-between items-center p-2 bg-white rounded border border-gray-200 text-xs mb-1">
                        <div className="flex items-center flex-grow">
                          <span className="text-gray-800">{item.title}</span>
                          <span className="text-gray-500 ml-2 text-xs">
                            {item.route}
                          </span>
                          <span className="text-gray-400 ml-2 text-xs">
                            (ID: {item.pageId || "нет"})
                          </span>
                          {item.route && (
                            <Link
                              to={`/admin/pages${item.route}`}
                              className="ml-2"
                              style={{ color: "var(--primary-color)" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink size={10} />
                            </Link>
                          )}
                        </div>
                        <div className="flex gap-1">
                          {item.pageId ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openModal("editPage", null, null, item.pageId);
                              }}
                              className="text-gray-400 hover:bg-gray-50 p-1 rounded"
                              style={{
                                "--hover-color": "var(--primary-color)",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.color = "var(--primary-color)")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.color = "#9ca3af")
                              }
                              title="Редактировать страницу"
                            >
                              <Edit size={12} />
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                alert(
                                  "У этого пункта меню нет связанной страницы. pageId = null"
                                );
                              }}
                              className="text-gray-300 p-1 rounded cursor-not-allowed"
                              title="Нет связанной страницы"
                            >
                              <Edit size={12} />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteMenuItem("item", item.id, parent.id);
                            }}
                            className="text-gray-400 hover:text-red-500 hover:bg-gray-50 p-1 rounded"
                          >
                            <Trash2 size={10} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center p-2 bg-white rounded border border-gray-200 mb-1 hover:bg-gray-50">
                          <div
                            className="flex items-center flex-grow cursor-pointer"
                            onClick={() => toggleExpand("section", item.id)}
                          >
                            {expandedItems[`section-${item.id}`] ? (
                              <ChevronDown
                                size={10}
                                className="mr-2"
                                style={{ color: "var(--primary-color)" }}
                              />
                            ) : (
                              <ChevronRight
                                size={10}
                                className="mr-2"
                                style={{ color: "var(--primary-color)" }}
                              />
                            )}
                            <span className="text-gray-800 text-xs">
                              {item.title}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteMenuItem("section", item.id, parent.id);
                            }}
                            className="text-gray-400 hover:text-red-500 hover:bg-gray-50 p-1 rounded"
                          >
                            <Trash2 size={10} />
                          </button>
                        </div>

                        {expandedItems[`section-${item.id}`] && (
                          <div className="ml-5 mt-1">
                            {item.items.map((subItem) => (
                              <div
                                key={subItem.id}
                                className="flex justify-between items-center p-2 bg-white rounded border border-gray-200 text-xs mb-1"
                              >
                                <div className="flex items-center flex-grow">
                                  <span className="text-gray-800">
                                    {subItem.title}
                                  </span>
                                  <span className="text-gray-500 ml-2 text-xs">
                                    {subItem.route}
                                  </span>
                                  <span className="text-gray-400 ml-2 text-xs">
                                    (ID: {subItem.pageId || "нет"})
                                  </span>
                                  {subItem.route && (
                                    <Link
                                      to={`/admin/pages${subItem.route}`}
                                      className="ml-2"
                                      style={{ color: "var(--primary-color)" }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ExternalLink size={10} />
                                    </Link>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  {subItem.pageId ? (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openModal(
                                          "editPage",
                                          null,
                                          null,
                                          subItem.pageId
                                        );
                                      }}
                                      className="text-gray-400 hover:bg-gray-50 p-1 rounded"
                                      onMouseEnter={(e) =>
                                      (e.target.style.color =
                                        "var(--primary-color)")
                                      }
                                      onMouseLeave={(e) =>
                                        (e.target.style.color = "#9ca3af")
                                      }
                                      title="Редактировать страницу"
                                    >
                                      <Edit size={12} />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        alert(
                                          "У этого пункта меню нет связанной страницы. pageId = null"
                                        );
                                      }}
                                      className="text-gray-300 p-1 rounded cursor-not-allowed"
                                      title="Нет связанной страницы"
                                    >
                                      <Edit size={12} />
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteMenuItem(
                                        "item",
                                        subItem.id,
                                        parent.id,
                                        item.id
                                      );
                                    }}
                                    className="text-gray-400 hover:text-red-500 hover:bg-gray-50 p-1 rounded"
                                  >
                                    <Trash2 size={10} />
                                  </button>
                                </div>
                              </div>
                            ))}

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openModal("item", parent.id, item.id);
                              }}
                              className="flex items-center p-2 text-xs hover:bg-gray-50 rounded"
                              style={{ color: "var(--primary-color)" }}
                            >
                              <Plus size={10} className="mr-1" />
                              Добавить пункт
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}

                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => openModal("item", parent.id)}
                    className="flex items-center p-2 text-xs hover:bg-gray-50 rounded"
                    style={{ color: "var(--primary-color)" }}
                  >
                    <Plus size={10} className="mr-1" />
                    Добавить пункт
                  </button>
                  <button
                    onClick={() => openModal("section", parent.id)}
                    className="flex items-center p-2 text-xs hover:bg-gray-50 rounded"
                    style={{ color: "var(--primary-color)" }}
                  >
                    <Plus size={10} className="mr-1" />
                    Добавить раздел
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        <button
          onClick={() => openModal("parent")}
          className="w-full mt-3 flex items-center justify-center p-2 bg-white border border-gray-200 text-gray-800 hover:bg-gray-50 rounded text-xs"
        >
          <Plus size={14} className="mr-2" />
          Добавить пункт меню
        </button>
      </div>

      <button
        onClick={fetchMenuData}
        className="mt-3 px-4 py-2 text-white rounded text-xs"
        style={{ backgroundColor: "var(--primary-color)" }}
        onMouseEnter={(e) => (e.target.style.filter = "brightness(0.9)")}
        onMouseLeave={(e) => (e.target.style.filter = "brightness(1)")}
      >
        Обновить меню
      </button>

      {modalState.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div
            className={`bg-white rounded-lg p-5 ${modalState.type === "item" || modalState.type === "editPage"
                ? "w-[550px]"
                : "w-96"
              } max-h-[85vh] overflow-y-auto shadow-xl`}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-800">
                {modalState.type === "parent" && "Добавить пункт меню"}
                {modalState.type === "section" && "Добавить раздел"}
                {modalState.type === "item" && "Добавить пункт"}
                {modalState.type === "editPage" && "Редактировать страницу"}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X size={16} />
              </button>
            </div>

            {loadingPageData ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader
                  size={24}
                  className="animate-spin"
                  style={{ color: "var(--primary-color)" }}
                />
                <p className="mt-3 text-gray-500 text-sm">
                  Загрузка данных страницы...
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Заголовки на разных языках
                  </label>
                  <div className="space-y-3">
                    {Object.entries(supportedLanguages).map(([code, name]) => (
                      <div key={code}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {name} ({code.toUpperCase()})
                        </label>
                        <input
                          type="text"
                          value={formData.titles[code] || ""}
                          onChange={(e) =>
                            handleTitleChange(code, e.target.value)
                          }
                          placeholder={`Введите заголовок на ${name.toLowerCase()}`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                          onFocus={(e) => {
                            e.target.style.borderColor = "var(--primary-color)";
                            e.target.style.boxShadow = `0 0 0 2px ${getComputedStyle(
                              document.documentElement
                            ).getPropertyValue("--primary-color")}33`;
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#d1d5db";
                            e.target.style.boxShadow = "none";
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {(modalState.type === "item" ||
                  modalState.type === "editPage") && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Маршрут
                      </label>
                      <input
                        type="text"
                        name="route"
                        value={formData.route || ""}
                        onChange={handleInputChange}
                        placeholder="/example-page"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2"
                        onFocus={(e) => {
                          e.target.style.borderColor = "var(--primary-color)";
                          e.target.style.boxShadow = `0 0 0 2px ${getComputedStyle(
                            document.documentElement
                          ).getPropertyValue("--primary-color")}33`;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#d1d5db";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                      {modalState.type === "editPage" && (
                        <p className="text-xs text-gray-500 mt-1">
                          Текущий маршрут: {formData.route || "Не установлен"}
                        </p>
                      )}
                    </div>
                  )}

                <button
                  onClick={saveMenuItem}
                  disabled={
                    !Object.values(formData.titles).some(
                      (title) => title && title.trim()
                    ) ||
                    ((modalState.type === "item" ||
                      modalState.type === "editPage") &&
                      !formData.route) ||
                    loadingPageData ||
                    isSubmitting
                  }
                  className="w-full py-3 text-white rounded-md disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                  style={{ backgroundColor: "var(--primary-color)" }}
                  onMouseEnter={(e) =>
                    !e.target.disabled &&
                    (e.target.style.filter = "brightness(0.9)")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.filter = "brightness(1)")
                  }
                >
                  {saveStatus === "saving" || isSubmitting ? "Сохранение..." : "Сохранить"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}